import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { format, parseISO } from "date-fns";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactScheduleModal from "../../components/ContactScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_SCHEDULES") {
        const schedules = action.payload;
        const newSchedules = [];

        schedules.forEach((schedule) => {
            const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
            if (scheduleIndex !== -1) {
                state[scheduleIndex] = schedule;
            } else {
                newSchedules.push(schedule);
            }
        });

        return [...state, ...newSchedules];
    }

    if (action.type === "UPDATE_SCHEDULES") {
        const schedule = action.payload;
        const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

        if (scheduleIndex !== -1) {
            state[scheduleIndex] = schedule;
            return [...state];
        } else {
            return [schedule, ...state];
        }
    }

    if (action.type === "DELETE_SCHEDULE") {
        const scheduleId = action.payload;
        return state.filter((s) => s.id !== scheduleId);
    }

    if (action.type === "RESET") {
        return [];
    }

    return state;
};

const ContactSchedules = () => {
    const classes = useStyles();
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [scheduleModalInitialValues, setScheduleModalInitialValues] = useState(null);
    const [deletingSchedule, setDeletingSchedule] = useState(null);
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [schedules, dispatch] = useReducer(reducer, []);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const fetchSchedules = async () => {
            try {
                const { data } = await api.get("/contact-schedules", {
                    params: { searchParam, pageNumber },
                });
                if (Array.isArray(data)) {
                    dispatch({ type: "LOAD_SCHEDULES", payload: data });
                    setHasMore(false);
                } else {
                    dispatch({ type: "LOAD_SCHEDULES", payload: data });
                }

                setLoading(false);
            } catch (err) {
                toastError(err);
                setLoading(false);
            }
        };
        fetchSchedules();
    }, [searchParam, pageNumber]);

    const handleOpenScheduleModal = () => {
        setSelectedSchedule(null);
        setScheduleModalInitialValues(null);
        setScheduleModalOpen(true);
    };

    const handleCloseScheduleModal = () => {
        setSelectedSchedule(null);
        setScheduleModalInitialValues(null);
        setScheduleModalOpen(false);
        setPageNumber(1);
        dispatch({ type: "RESET" });
        setSearchParam((prev) => prev ? prev + " " : " ");
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditSchedule = (schedule) => {
        setSelectedSchedule(schedule);
        setScheduleModalInitialValues(null);
        setScheduleModalOpen(true);
    };

    const handleCloneSchedule = (schedule) => {
        setScheduleModalInitialValues({
            ...schedule,
            id: null,
            scheduledFor: "",
        });
        setSelectedSchedule(null);
        setScheduleModalOpen(true);
    };

    const handleDeleteSchedule = async (scheduleId) => {
        try {
            await api.delete(`/contact-schedules/${scheduleId}`);
            toast.success(i18n.t("schedules.toasts.deleted"));
            dispatch({ type: "DELETE_SCHEDULE", payload: scheduleId });
        } catch (err) {
            toastError(err);
        }
        setDeletingSchedule(null);
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    const formatRecursion = (schedule) => {
        if (!schedule.isRecurring) return "Não";
        return `A cada ${schedule.recursionInterval} dias` + (schedule.recursionEndDate ? ` até ${format(parseISO(schedule.recursionEndDate), "dd/MM/yyyy")}` : "");
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title={deletingSchedule && `${i18n.t("schedules.confirmationModal.deleteTitle")}`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
            >
                Deseja realmente excluir este agendamento?
            </ConfirmationModal>
            <ContactScheduleModal
                open={scheduleModalOpen}
                onClose={handleCloseScheduleModal}
                scheduleId={selectedSchedule ? selectedSchedule.id : null}
                contactId={selectedSchedule ? selectedSchedule.contactId : null}
                initialValues={scheduleModalInitialValues}
            />
            <MainHeader>
                <Title>Agendamentos de Mensagens</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder={i18n.t("schedules.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenScheduleModal}
                    >
                        Cadastrar Agendamento
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">Contato</TableCell>
                            <TableCell align="center">Data Programada</TableCell>
                            <TableCell align="center">Mensagem</TableCell>
                            <TableCell align="center">Recorrência</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">{i18n.t("schedules.table.actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {schedules.map((schedule) => (
                                <TableRow key={schedule.id}>
                                    <TableCell align="center">{schedule.contact?.name}</TableCell>
                                    <TableCell align="center">
                                        {format(parseISO(schedule.scheduledFor), "dd/MM/yyyy HH:mm")}
                                    </TableCell>
                                    <TableCell align="center">
                                        {schedule.message.length > 50 ? `${schedule.message.substring(0, 50)}...` : schedule.message}
                                    </TableCell>
                                    <TableCell align="center">{formatRecursion(schedule)}</TableCell>
                                    <TableCell align="center" style={{
                                        color: schedule.status === 'pending' ? 'orange' :
                                            schedule.status === 'sent' ? 'green' : 'red'
                                    }}>
                                        {schedule.status === 'pending' ? 'Pendente' :
                                            schedule.status === 'sent' ? 'Enviado' : 'Erro'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {schedule.status === 'pending' && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditSchedule(schedule)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        )}

                                        <IconButton
                                            size="small"
                                            onClick={() => handleCloneSchedule(schedule)}
                                            title="Clonar Agendamento"
                                        >
                                            <FileCopyIcon />
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setDeletingSchedule(schedule);
                                                setConfirmModalOpen(true);
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={6} />}
                        </>
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default ContactSchedules;
